// Prompts ported character for character from ios/Depth/Levels/Prompts.swift (DEPTH.md §3), with the structure
// repair (parseStructure) the apps share (tools/testdata/depth/repair_cases.json).
import { escape } from "./markup";
import type { Beat, Section, Structure } from "./types";

export interface PromptParagraph { text: string }
export interface PromptChapter { title: string; paragraphs: PromptParagraph[] }

export class BadResponse extends Error {
  constructor(why: string) { super("The model's answer couldn't be read. " + why); }
}

export function structure(bookTitle: string, author: string, chapter: PromptChapter): { system: string; user: string } {
  const n = chapter.paragraphs.length;
  const target = Math.max(1, swiftRound(n / 3.5));
  const numbered = chapter.paragraphs.map((p, i) => `[${i + 1}] ${p.text}`).join("\n\n");
  const by = author === "" ? "" : ` by ${author}`;
  const user = `Book: ${bookTitle}${by}
Chapter: ${chapter.title}
The chapter has ${n} numbered paragraphs:

${numbered}

Build three layers for this chapter.

1. "beats": split the chapter into consecutive passages. A beat is one unit of meaning: one exchange of dialogue, one step in an argument, one moment in a scene. Group runs of short dialogue lines together; a long paragraph can be a beat on its own. Aim for about ${target} beats. Every paragraph belongs to exactly one beat, in order, with no gaps or overlaps.
   For each beat give "from" and "to" (paragraph numbers, inclusive) and "gist": one plain sentence of at most 25 words saying what happens or what is argued. Use the present tense and name people instead of using pronouns.

2. "sections": group consecutive beats into 3 to 5 sections, each covering at least two beats (a very short chapter can have 2 sections). Sections are the chapter's outline, so each should be a real stage of the chapter, not a single exchange. For each give "label" (a 2–4 word subheading), "summary" (one sentence, at most 30 words) and "beats": [first, last] as zero-based beat indexes, inclusive. Sections cover every beat, in order.

3. "chapter_summary": two or three sentences summarising the chapter.

Also give "kind": "fiction" or "nonfiction".

Describe only what is in these paragraphs. Never mention anything that happens later in the book, even if you know the book.

Return exactly this JSON shape:
{"kind":"fiction","beats":[{"from":1,"to":2,"gist":"..."}],"sections":[{"label":"...","summary":"...","beats":[0,1]}],"chapter_summary":"..."}`;
  return { system: "You build layered reading guides for chapters of books. You return JSON only.", user };
}

/** Swift's Double.rounded(): half away from zero. */
function swiftRound(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

/** Swift `int(_:)`: Int, Double (truncated) or a string that is an integer. */
function int(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "boolean") return v ? 1 : 0; // NSNumber bridging
  if (typeof v === "string" && /^[+-]?\d+$/.test(v)) return parseInt(v, 10);
  return null;
}

const trimWS = (s: string) => s.replace(/^[\s\u0085]+|[\s\u0085]+$/g, "");

/** Parses the model's JSON and repairs it so beats and sections cover everything exactly once. */
export function parseStructure(text: string, n: number): Structure {
  const obj = jsonObject(text);
  if (!obj) throw new BadResponse("No JSON object.");
  const rawBeats = (Array.isArray(obj.beats) ? obj.beats : []).flatMap((b: any) => {
    if (!b || typeof b !== "object") return [];
    const f = int(b.from), t = int(b.to), g = b.gist;
    if (f === null || t === null || typeof g !== "string" || g === "") return [];
    return [{ from: f, to: t, gist: trimWS(g) }];
  });
  rawBeats.sort((a: Beat, b: Beat) => a.from - b.from);
  if (!rawBeats.length) throw new BadResponse("No beats.");

  const beats: Beat[] = [];
  let next = 1;
  for (const b of rawBeats) {
    if (next > n) break;
    if (b.from > n) continue;
    if (b.to < next) continue;
    if (b.from > next && beats.length) { beats[beats.length - 1].to = b.from - 1; next = b.from; }
    const to = Math.min(n, b.to);
    beats.push({ from: next, to, gist: b.gist });
    next = to + 1;
  }
  beats[beats.length - 1].to = n;
  const zero = beats.map((b) => ({ from: b.from - 1, to: b.to - 1, gist: b.gist }));
  const count = zero.length;

  const rawSections = (Array.isArray(obj.sections) ? obj.sections : []).flatMap((s: any) => {
    if (!s || typeof s !== "object") return [];
    const range = s.beats;
    if (typeof s.label !== "string" || typeof s.summary !== "string" || !Array.isArray(range) || !range.length) return [];
    const a = int(range[0]), b = int(range[range.length - 1]);
    if (a === null || b === null) return [];
    return [{ label: s.label.trim(), summary: s.summary.trim(), a, b }];
  });
  rawSections.sort((x: { a: number }, y: { a: number }) => x.a - y.a);
  let sections: Section[] = [];
  let nb = 0;
  for (const s of rawSections) {
    if (nb >= count) break;
    if (s.a >= count) continue;
    if (s.b < nb) continue;
    if (s.a > nb && sections.length) { sections[sections.length - 1].lastBeat = s.a - 1; nb = s.a; }
    const b = Math.min(count - 1, s.b);
    sections.push({ label: s.label, summary: s.summary, firstBeat: nb, lastBeat: b });
    nb = b + 1;
  }
  if (!sections.length) {
    const size = Math.max(2, Math.ceil(count / 4));
    sections = [];
    for (let i = 0; i < count; i += size) {
      sections.push({ label: `Part ${i / size + 1}`, summary: zero[i].gist, firstBeat: i, lastBeat: Math.min(count - 1, i + size - 1) });
    }
  }
  sections[sections.length - 1].lastBeat = count - 1;
  return {
    kind: obj.kind === "nonfiction" ? "nonfiction" : "fiction",
    beats: zero, sections,
    summary: typeof obj.chapter_summary === "string" ? obj.chapter_summary : "",
  };
}

export function note(args: { bookTitle: string; author: string; textSoFar: string; chapterTitle: string; before: string;
  passage: string; fiction: boolean; explained: string[] }): { system: string; user: string } {
  const { bookTitle, author, textSoFar, chapterTitle, before, passage, fiction, explained } = args;
  const guidance = fiction
    ? "Help the reader follow and enjoy it: period customs, words or references a modern reader might miss; reminders of earlier events or characters the passage relies on; and subtext the text itself signals, such as narrator irony or a character teasing."
    : "Explain the ideas: define technical terms, supply the background or example the author assumes, and connect the passage to the argument so far.";
  const by = author === "" ? "" : ` by ${author}`;
  const explainedRule = explained.length === 0 ? "" : `\n- These terms were already explained to the reader; don't define them again: ${explained.join(", ")}.`;
  const user = `Book: ${bookTitle}${by}

<text_so_far>
${textSoFar}## ${chapterTitle}

${before}
</text_so_far>

<passage>
${passage}
</passage>

Write a note of 35 to 70 words about the passage. ${guidance}

Rules:
- Write for a first-time reader who has read only the text above. Never reveal or hint at anything that happens after the passage, even if you know the book.
- Judge characters only by what the text so far shows. If the text has not yet revealed that someone is lying, scheming or hiding something, don't suggest it: no hints, no foreshadowing, no "note that". Present their claims as the reader currently experiences them.${explainedRule}
- Don't retell the passage; the reader just read it. Add something they wouldn't get on their own.
- Plain, direct sentences. Start with the substance, not with "This passage" or "Here".
- Wrap each term you define in <b></b>. Use no other markup.`;
  return { system: "You are a reading companion inside an e-reader. When the reader zooms in on a passage, you write a short note that helps them understand it more deeply.", user };
}

export function check(before: string, passage: string, draft: string, explained: string[]): { system: string; user: string } {
  const explainedRule = explained.length === 0 ? "" : `\n- defines any of these terms, which the reader already had explained: ${explained.join(", ")}`;
  const user = `<text_so_far>
${before}
</text_so_far>

<passage>
${passage}
</passage>

<note>
${draft}
</note>

The note will be shown to a first-time reader who has read only the text above. Check every claim in the note against that text.

Remove or rewrite anything that:
- says or hints that a character is lying, scheming, manipulating, testing, baiting or hiding something, unless the text so far clearly shows it (the narrator says so, or it's been exposed)
- judges a character's true nature beyond what the text so far shows
- refers to events, revelations or outcomes that are not in the text so far${explainedRule}

Keep everything else, including period customs, word meanings and reminders of earlier events. Keep the same voice and <b></b> markup, and keep it between 30 and 70 words. If nothing needs changing, return the note unchanged.

Return {"problems":["short description of each problem, or empty"],"note":"the final note"}`;
  return { system: "You are a spoiler editor for an e-reader. You return JSON only.", user };
}

export function parseCheck(text: string, fallback: string): string {
  const obj = jsonObject(text);
  return obj && typeof obj.note === "string" && obj.note !== "" ? obj.note : fallback;
}

/** Model output is plain text plus <b>; escape everything else so it renders safely. */
export function sanitizeNote(s: string): string {
  let t = trimWS(s);
  t = t.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
  t = escape(t);
  return t.replaceAll("&lt;b&gt;", "<b>").replaceAll("&lt;/b&gt;", "</b>");
}

export function jsonObject(text: string): any | null {
  const start = text.indexOf("{"), end = text.lastIndexOf("}");
  if (start < 0 || end < 0 || start >= end) return null;
  try {
    const v = JSON.parse(text.slice(start, end + 1));
    return v && typeof v === "object" && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}
